<script setup>
import { createBugReportValidator } from '~/validators/bugReport.js'
import TextareaInput from '~/components/Forms/TextareaInput.vue'
import FormSelect from '~/components/Forms/FormSelect.vue'
import CustomButton from '~/components/UI/CustomButton.vue'
import { useBugReportApi } from '~/composables/api/useBugReportApi.js'
import { useScreenshotCapture } from '~/composables/useScreenshotCapture.js'

const emit = defineEmits(['close'])
const { t } = useI18n()
const { add: addToast } = useToast()
const { sendBugReport, uploadScreenshot } = useBugReportApi()
const { captureFullPage, capturing } = useScreenshotCapture()

const isSubmitting = ref(false)
const isCapturing = ref(false)
const screenshotTempPath = ref('')
const screenshotPreview = ref('')

const panelRef = ref(null)
const isDragging = ref(false)
const dragPosition = ref({ x: 0, y: 0 })
const dragStart = ref({ x: 0, y: 0, panelX: 0, panelY: 0, width: 0, height: 0 })

const form = ref({
  type: 'bug',
  description: ''
})

const errors = ref({
  type: '',
  description: ''
})

const typeOptions = computed(() => [
  { optionValue: 'bug', optionId: t('bug_report.type_bug') },
  { optionValue: 'improvement', optionId: t('bug_report.type_improvement') },
  { optionValue: 'other', optionId: t('bug_report.type_other') }
])

const validateForm = () => {
  const result = createBugReportValidator(form.value)
  if (!result.success) {
    errors.value = result.errors
    return false
  }
  errors.value = {}
  return true
}

const ignoreModalElement = (element) => {
  return element.closest('[data-bug-report-modal]') !== null
}

const handleCapture = async () => {
  isCapturing.value = true
  screenshotTempPath.value = ''
  screenshotPreview.value = ''

  try {
    const file = await captureFullPage({ ignoreElements: ignoreModalElement })

    if (!file) {
      addToast(t('bug_report.capture_failed_description'), 'error')
      return
    }

    const tempPath = await uploadScreenshot(file)
    screenshotTempPath.value = tempPath
    screenshotPreview.value = URL.createObjectURL(file)

    addToast(t('bug_report.capture_success_description'), 'success')
  } catch (error) {
    addToast(error?.message || t('bug_report.capture_failed_description'), 'error')
  } finally {
    isCapturing.value = false
  }
}

const removeScreenshot = () => {
  if (screenshotPreview.value) {
    URL.revokeObjectURL(screenshotPreview.value)
  }
  screenshotTempPath.value = ''
  screenshotPreview.value = ''
}

const buildBrowserMetadata = () => {
  return {
    url: window.location.href,
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    language: window.navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    cookiesEnabled: window.navigator.cookieEnabled,
    timestamp: new Date().toISOString()
  }
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true

  try {
    await sendBugReport({
      type: form.value.type,
      description: form.value.description,
      screenshotTempPath: screenshotTempPath.value || undefined,
      metadata: {
        browser: buildBrowserMetadata()
      }
    })

    addToast(t('bug_report.success_description'), 'success')

    removeScreenshot()
    form.value.description = ''
    form.value.type = 'bug'
    emit('close')
  } catch (error) {
    addToast(error?.message || t('bug_report.error_description'), 'error')
  } finally {
    isSubmitting.value = false
  }
}

const handleClose = () => {
  removeScreenshot()
  emit('close')
}

const startDrag = (event) => {
  if (!panelRef.value) return
  if (event.target.closest('button')) return

  const rect = panelRef.value.getBoundingClientRect()
  isDragging.value = true
  dragStart.value = {
    x: event.clientX - dragPosition.value.x,
    y: event.clientY - dragPosition.value.y,
    panelX: rect.left - dragPosition.value.x,
    panelY: rect.top - dragPosition.value.y,
    width: rect.width,
    height: rect.height
  }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

const onDrag = (event) => {
  if (!isDragging.value) return

  const newX = event.clientX - dragStart.value.x
  const newY = event.clientY - dragStart.value.y

  const minX = -dragStart.value.panelX
  const maxX = window.innerWidth - dragStart.value.panelX - dragStart.value.width
  const minY = -dragStart.value.panelY
  const maxY = window.innerHeight - dragStart.value.panelY - dragStart.value.height

  dragPosition.value = {
    x: Math.max(minX, Math.min(maxX, newX)),
    y: Math.max(minY, Math.min(maxY, newY))
  }
}

const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

const handleEscape = (event) => {
  if (event.key === 'Escape') {
    handleClose()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscape)
  removeScreenshot()
})
</script>

<template>
  <div
    v-show="!isCapturing"
    data-bug-report-modal
    class="fixed inset-0 z-50 flex items-end justify-end p-4"
  >
    <div
      ref="panelRef"
      :style="{ transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)` }"
      class="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 pointer-events-auto flex flex-col max-h-[90vh]"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 cursor-move select-none"
        @mousedown="startDrag"
      >
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ t('bug_report.modal_title') }}
        </h3>
        <button
          @click="handleClose"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          :aria-label="t('common.close')"
        >
          <Icon name="mdi:close" class="text-xl" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate class="flex flex-col flex-1 min-h-0">
        <div class="p-4 overflow-y-auto space-y-4">
          <FormSelect
            v-model="form.type"
            name="bug-report-type"
            :label="t('bug_report.field_type')"
            :options="typeOptions"
            :error-msg="errors.type"
          />

          <TextareaInput
            v-model="form.description"
            name="bug-report-description"
            :label="t('bug_report.field_description')"
            :placeholder="t('bug_report.description_placeholder')"
            :help-text="t('bug_report.description_help')"
            :error-msg="errors.description"
            :rows="5"
          />

          <div>
            <p class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {{ t('bug_report.field_screenshot') }}
            </p>

            <div class="flex items-center gap-3">
              <button
                v-if="!screenshotPreview"
                type="button"
                @click="handleCapture"
                :disabled="capturing"
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Icon v-if="capturing" name="eos-icons:three-dots-loading" />
                <Icon v-else name="mdi:camera-plus" />
                {{ capturing ? t('bug_report.capturing') : t('bug_report.capture_button') }}
              </button>

              <div v-else class="relative flex items-center gap-3">
                <img
                  :src="screenshotPreview"
                  alt="Screenshot"
                  class="h-16 w-auto rounded border border-slate-200 dark:border-slate-700 object-cover"
                />
                <button
                  type="button"
                  @click="removeScreenshot"
                  class="text-rose-500 hover:text-rose-700 text-sm flex items-center gap-1"
                >
                  <Icon name="mdi:trash-can-outline" />
                  {{ t('bug_report.remove_screenshot') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <CustomButton
            type="button"
            variant="secondary"
            @click="handleClose"
          >
            {{ t('common.cancel') }}
          </CustomButton>
          <CustomButton
            type="submit"
            :loading="isSubmitting"
            @click="handleSubmit"
          >
            {{ t('bug_report.submit_button') }}
          </CustomButton>
        </div>
      </form>
    </div>
  </div>
</template>
